<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor/Indentation';
    let nextLink= '/Documentation/Defining_an_Editor/Boolean_Projections';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="ease-of-editing-1"
  bind:intersecting={visibleById["ease-of-editing-1"]}
  onIntersectChange={(visible) => setVisible("ease-of-editing-1", visible)}
>Ease of Editing</SectionComponent>
<p>As a language engineer, you can make editing a little bit easier for your users. Freon offers two options to do so.</p>
<SectionComponent tag="h2" 
  id="reference-shortcuts-2"
  bind:intersecting={visibleById["reference-shortcuts-2"]}
  onIntersectChange={(visible) => setVisible("reference-shortcuts-2", visible)}
>Reference Shortcuts</SectionComponent>
<p>In the actual abstract syntax tree, a reference property is stored as a node that holds the information about,
and a link to, the referred node. The type of this node is <code>FreNodeReference</code>. When creating a new reference property,
this means that two actions need to be executed by Freon. First, the <code>FreNodeReference</code> instance needs to be
created, and second, the link needs to be established between this <code>FreNodeReference</code> instance and the node that
is referred to.</p>
<p>To hide exposing this two-step process to the user, the language engineer may define a <strong>reference shortcut</strong>.
This is a property defined directly on the concept, and when selected by the user causes this two-step
process to be performed as one. This can be useful, especially when dealing with expressions. </p>
<p>Our running example provides the expression concept <code>InsurancePartRef</code>.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-expressions.ast#L54-L57

// Expression used to refer to the insurance parts of a product
expression InsurancePartRef base DocuExpression &#123;
    reference part: InsurancePart;
&#125;</code></pre>
<p>For this concept the following projection is defined.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-expressions-default.edit#L35-L38

InsurancePartRef &#123;
    [$&#123;self.part&#125;]
    referenceShortcut = $&#123;self.part&#125;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="triggers-3"
  bind:intersecting={visibleById["triggers-3"]}
  onIntersectChange={(visible) => setVisible("triggers-3", visible)}
>Triggers</SectionComponent>
<p>When the user wants to create an instance of a concept using the keyboard, a special character or string
of characters can be used to trigger the creation of that instance. This makes entering expressions in the
projectional editor feel more like typing them in a text-based editor.
The character or string of characters is called a <strong>trigger</strong>.</p>
<p>The example shows a trigger for entering a function call expression.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/editor-expressions-default.edit#L30-L34

FunctionCallExpression &#123;
    [$&#123;funcDefinition&#125; ( $&#123;args horizontal separator[,] &#125; )]
    referenceShortcut = $&#123;funcDefinition&#125;
    trigger = "calc"
&#125;</code></pre>


<PrevNextSection {prevLink} {nextLink} />